// Relative, not via the `@Translations` alias: that alias is absent from jest's
// moduleNameMapper, and `useGlobalization` reads the same file the same way.
import { ns } from '../i18n/settings.json';
import { TaskType } from '@Types/inspection.types';
import { t } from 'i18next';

/**
 * This library's keys live in its own i18next namespace, but the bare `t`
 * exported from i18next resolves against the *host app's* default namespace.
 * Every message below therefore has to name the namespace explicitly, the way
 * `useGlobalization` does for components.
 *
 * Without it these keys silently fall back to the English literal: the values
 * added to this library's own locale files are never consulted. That is easy to
 * miss, because "Inspection Updated Successfully" appears translated today only
 * because app-am-activities happens to carry a copy of the same key in its own
 * bundle - a coincidence, not a mechanism, and one no new key inherits.
 */
const options = { ns };

/**
 * Picks the confirmation toast for a successful update.
 *
 * Inspections, tasks and events share one action, one endpoint and one saga, so
 * the toast has to be chosen from the payload. `recordType` is the only field
 * that distinguishes them - `standard` carries the record's configuration
 * standard for tasks and events, so it cannot be used here.
 *
 * Each message is a separate literal passed straight to `t()`. i18next-parser
 * only extracts a literal first argument, so folding these into one `t()` with a
 * ternary or an interpolated noun would silently leave them untranslated in
 * every locale but English.
 */
export const getUpdateSuccessMessage = (recordType?: TaskType): string => {
	switch (recordType) {
		case TaskType.Task:
			return t('Task Updated Successfully', options);
		case TaskType.Event:
			return t('Event Updated Successfully', options);
		default:
			return t('Inspection Updated Successfully', options);
	}
};

/**
 * The failure counterpart, for the same reasons.
 *
 * This replaces a generic `{{message}} Failed: {{reason}}` frame that took the
 * subject as an interpolation value. That value was a bare English string, so
 * it was never passed through `t()` and never appeared in the translation
 * files - a German user saw a translated frame wrapped around "Inspection
 * Update". Whole sentences are used here instead of a noun slotted into a
 * frame, so each locale controls its own word order.
 */
export const getUpdateFailureMessage = (
	reason: string,
	recordType?: TaskType,
): string => {
	switch (recordType) {
		case TaskType.Task:
			return t('Task Update Failed: {{reason}}', { ...options, reason });
		case TaskType.Event:
			return t('Event Update Failed: {{reason}}', { ...options, reason });
		default:
			return t('Inspection Update Failed: {{reason}}', {
				...options,
				reason,
			});
	}
};
